import type { TodayResponse } from '@xox/shared'
import { useEffect } from 'react'
import { create } from 'zustand'
import { api } from '../lib/api'

/** Re-visits within this window reuse the last answer; a forced load always goes out. */
const FRESH_MS = 5_000

interface TodayState {
  data: TodayResponse | null
  error: string | null
  loading: boolean
  loadedAt: number
  load: (force?: boolean) => Promise<void>
}

export const useToday = create<TodayState>((set, get) => ({
  data: null,
  error: null,
  loading: false,
  loadedAt: 0,
  async load(force = false) {
    const { loading, loadedAt } = get()
    if (loading || (!force && Date.now() - loadedAt < FRESH_MS)) return
    set({ loading: true })
    try {
      set({ data: await api.today(), error: null, loading: false, loadedAt: Date.now() })
    } catch (err) {
      set({ error: (err as Error).message, loading: false })
    }
  },
}))

/** Loads the Today data on mount and whenever the tab comes back into view. */
export function useLoadToday() {
  const load = useToday((s) => s.load)
  useEffect(() => {
    void load()
    const onVisible = () => {
      if (!document.hidden) void load()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [load])
}
